/**
 * Did a thrown error come from our own bundle, or from a script somebody else
 * injected into the page?
 *
 * BACKGROUND
 * On 2026-07-31 two visitors hit `TypeError: Converting circular structure to
 * JSON` on /services and /client/login. Reading the stack bottom-up: React's
 * appendAllChildren called appendChild on an <li>, and appendChild resolved to
 * line 103 of an anonymous script which chained to line 45, then line 25, then
 * JSON.stringify on the node being appended. Stringifying an anchor walks
 * __reactFiber$… -> stateNode and closes a cycle.
 *
 * Something had replaced appendChild. URP never patches DOM prototypes — the
 * only stringify near the DOM is in seo.js and it serialises plain objects into
 * document.head. The frames were `<anonymous>` at lines 25/45/103, which is a
 * small injected file, not a webpack bundle. So: browser extension, injecting
 * proxy, or an instrumented headless browser.
 *
 * This is used ONLY to tag Sentry events, never to decide what a user sees. A
 * wrong answer mislabels an event; it cannot break a page. Keep it that way.
 */

/** Frame markers that mean "this is our code". */
const OURS = ['/static/js/', 'webpack'];

/** A frame that names a source file of any origin. */
const LOCATED = /https?:\/\/|\.jsx?:|\.tsx?:/;

/**
 * @param {Error} error
 * @param {string} [origin] - injectable for tests; defaults to window origin
 * @returns {boolean} true when nothing in the stack belongs to us
 */
export function isLikelyThirdParty(error, origin) {
  const stack = (error && error.stack) || '';
  if (!stack) return false;

  const site = origin !== undefined
    ? origin
    : ((typeof window !== 'undefined' && window.location && window.location.origin) || '');

  // Drop the first line — it is the message, not a frame.
  const frames = stack.split('\n').slice(1).map(f => f.trim()).filter(Boolean);
  if (!frames.length) return false;

  const located = frames.filter(f => LOCATED.test(f));
  // Every frame is anonymous. We cannot tell, so we do not guess.
  if (!located.length) return false;

  const ours = located.filter(f =>
    (site && f.includes(site)) || OURS.some(marker => f.includes(marker))
  );
  return ours.length === 0;
}

export default isLikelyThirdParty;
